/**
 * Gigabot Constants
 *
 * Centralized constants for the Gigabot component system.
 * Includes action statuses, message types, keyboard shortcuts, and configuration values.
 */

// ============================================================================
// ACTION PROPOSAL STATUSES
// ============================================================================

/**
 * Action proposal lifecycle statuses
 */
export const ACTION_STATUS = {
  PENDING: 'pending',
  RUNNING: 'running',
  COMPLETED: 'completed',
  SKIPPED: 'skipped',
  ERROR: 'error',
};

/**
 * Status display messages
 */
export const ACTION_STATUS_MESSAGES = {
  [ACTION_STATUS.RUNNING]: 'Applying changes...',
  [ACTION_STATUS.COMPLETED]: 'Changes applied successfully',
  [ACTION_STATUS.SKIPPED]: 'Action skipped',
  [ACTION_STATUS.ERROR]: 'Failed to apply changes',
};

// ============================================================================
// ACTION PROPOSAL TYPES
// ============================================================================

/**
 * Types of actions that can be proposed
 */
export const ACTION_TYPE = {
  COMMAND: 'command',
  API_CALL: 'api_call',
  PROFILE_UPDATE: 'profile_update',
  DATA_CHANGE: 'data_change',
  FILE_OPERATION: 'file_operation',
  DATABASE: 'database',
  DEPLOYMENT: 'deployment',
  CONFIGURATION: 'configuration',
};

/**
 * Action types that require user approval (Run/Skip)
 * SDUI actions like scroll_to, field_update are automatic UI hints, not user-approvable
 */
export const APPROVABLE_ACTION_TYPES = [
  'command',
  'api_call',
  'profile_update',
  'data_change',
  'file_change',
  'create',
  'update',
  'delete',
];

// ============================================================================
// MESSAGE TYPES
// ============================================================================

/**
 * Chat message types
 */
export const MESSAGE_TYPE = {
  USER: 'user',
  ASSISTANT: 'assistant',
  SYSTEM: 'system',
  ERROR: 'error',
};

// ============================================================================
// KEYBOARD SHORTCUTS
// ============================================================================

/**
 * Keyboard event keys
 */
export const KEY = {
  ENTER: 'Enter',
  ESCAPE: 'Escape',
  TAB: 'Tab',
  SPACE: ' ',
  ARROW_UP: 'ArrowUp',
  ARROW_DOWN: 'ArrowDown',
  L: 'l', // For Cmd+L shortcut
  K: 'k', // For Cmd+K shortcut
};

/**
 * Keyboard shortcuts configuration
 */
export const KEYBOARD_SHORTCUTS = {
  SEND_MESSAGE: KEY.ENTER, // Without Shift
  NEW_LINE: `Shift+${KEY.ENTER}`,
  RUN_ACTION: KEY.ENTER,
  SKIP_ACTION: KEY.ESCAPE,
  FOCUS_INPUT: `Cmd+K`,
  TOGGLE_GIGABOT: `Cmd+L`,
};

// ============================================================================
// UI CONFIGURATION
// ============================================================================

/**
 * Gigabot panel dimensions
 */
export const PANEL_DIMENSIONS = {
  MIN_WIDTH: 320,
  MAX_WIDTH: 800,
  DEFAULT_WIDTH: 480,
  MIN_HEIGHT: 400,
};

/**
 * Timing constants (in milliseconds)
 */
export const TIMING = {
  FOCUS_DELAY: 0,
  COPY_FEEDBACK_DURATION: 2000,
  AUTO_CLEANUP_DELAY: 5000,
  DEBOUNCE_DELAY: 300,
  ANIMATION_DURATION: 200,
  STREAMING_TIMEOUT: 30000,
  SCROLL_DEBOUNCE: 150,
};

/**
 * Template blank placeholder
 */
export const TEMPLATE_BLANK = '_____';
export const TEMPLATE_BLANK_LENGTH = 5;

/**
 * Pagination constants
 */
export const PAGINATION = {
  MAX_MESSAGES_PER_PAGE: 50,
  HISTORY_PER_PAGE: 20,
};

// ============================================================================
// DEFAULT VALUES
// ============================================================================

/**
 * Default messages
 */
export const DEFAULT_MESSAGES = {
  WELCOME: "Hello! I'm Gigabot, your AI assistant. How can I help you today?",
  PLACEHOLDER: 'Ask me anything...',
  NO_PREVIEW: 'No preview available',
  LOADING: 'Loading...',
  ERROR: 'Something went wrong. Please try again.',
};

/**
 * Default ARIA labels
 */
export const ARIA_LABELS = {
  GIGABOT_PANEL: 'Gigabot chat panel',
  SEND_MESSAGE: 'Send message',
  CLOSE_PANEL: 'Close Gigabot',
  OPEN_PANEL: 'Open Gigabot',
  RUN_ACTION: 'Run this action',
  SKIP_ACTION: 'Skip this action',
  COPY_COMMAND: 'Copy command',
  CLOSE_ACTION: 'Close action',
  MESSAGE_INPUT: 'Message input',
};

/**
 * CSS class prefixes
 */
export const CSS_CLASS = {
  ACTION_PROPOSAL: 'action-proposal',
  STATUS_PREFIX: 'action-proposal--',
  MESSAGE_RENDERER: 'message-renderer',
  GIGABOT_CONTAINER: 'gigabot-container',
};

// ============================================================================
// VALIDATION
// ============================================================================

/**
 * Content length limits
 */
export const LIMITS = {
  MAX_MESSAGE_LENGTH: 5000,
  MAX_PROPOSAL_TITLE_LENGTH: 100,
  MAX_PROPOSAL_DESCRIPTION_LENGTH: 500,
  MIN_CONTENT_LENGTH_FOR_DISPLAY: 50,
  MAX_TEXTAREA_HEIGHT: 200,
  SCROLL_THRESHOLD: 100,
};

// ============================================================================
// REGEX PATTERNS
// ============================================================================

/**
 * Pattern matching
 */
export const PATTERNS = {
  TEMPLATE_BLANK: /_____/g,
  URL: /^https?:\/\/.+/,
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
};

// ============================================================================
// API CONFIGURATION
// ============================================================================

/**
 * API endpoints (relative to base URL)
 */
export const API_ENDPOINTS = {
  CHAT: '/ai/chat',
  ACTION_RESPONSE: '/ai/action-response',
};

/**
 * SSE (Server-Sent Events) configuration
 */
export const SSE_CONFIG = {
  EVENT_MESSAGE: 'message',
  EVENT_ERROR: 'error',
  EVENT_DONE: 'done',
};

// ============================================================================
// FEATURE FLAGS
// ============================================================================

/**
 * Feature toggles
 */
export const FEATURES = {
  ENABLE_ACTION_PROPOSALS: true,
  ENABLE_STREAMING: true,
  ENABLE_KEYBOARD_SHORTCUTS: true,
  ENABLE_AUTO_CLEANUP: false, // Changed to false to keep completed actions visible
  ENABLE_COPY_BUTTON: true,
  ENABLE_RESIZE: true,
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if status is actionable (can run/skip)
 */
export const isActionableStatus = (status) => {
  return status === ACTION_STATUS.PENDING;
};

/**
 * Check if status is final (completed, skipped, error)
 */
export const isFinalStatus = (status) => {
  return [
    ACTION_STATUS.COMPLETED,
    ACTION_STATUS.SKIPPED,
    ACTION_STATUS.ERROR,
  ].includes(status);
};

/**
 * Get CSS class for status
 */
export const getStatusClass = (status) => {
  return `${CSS_CLASS.STATUS_PREFIX}${status}`;
};

/**
 * Get status message
 */
export const getStatusMessage = (status) => {
  return ACTION_STATUS_MESSAGES[status] || '';
};
